import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import EditProfile from "./pages/EditProfile";
import Connect from "./pages/Connect";
import ProfileView from "./pages/ProfileView";
import Connections from "./pages/Connections";
import Messages from "./pages/Messages";
import ConversationView from "./pages/ConversationView";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import SubjectRights from "./pages/SubjectRights";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Feedback from "./pages/Feedback";
import TermsOfService from "./pages/TermsOfService";
import LegalGuidance from "./pages/LegalGuidance";
import CandidateDashboard from "./pages/CandidateDashboard";
import EmployerDashboard from "./pages/EmployerDashboard";
import AdminImpersonation from "./pages/AdminImpersonation";
import AdminApprovals from "./pages/AdminApprovals";
import CommunityOrgAuth from "./pages/CommunityOrgAuth";
import JobSeekerAuth from "./pages/JobSeekerAuth";
import EmployerAuth from "./pages/EmployerAuth";
import JobSeekerSignup from "./pages/JobSeekerSignup";
import EmployerSignup from "./pages/EmployerSignup";
import CommunityOrgSignup from "./pages/CommunityOrgSignup";
import PostJob from "./pages/PostJob";
import FindTalent from "./pages/FindTalent";
import JobSearch from "./pages/JobSearch";
import JobApplication from "./pages/JobApplication";
import AIResumeBuilder from "./pages/AIResumeBuilder";
import ProgressiveDisclosure from "./pages/ProgressiveDisclosure";
import ProgressiveDisclosureInfo from "./pages/ProgressiveDisclosureInfo";
import InterviewPrep from "./pages/InterviewPrep";
import ProtectedRoute from "./components/ProtectedRoute";
import { ImpersonationBanner } from "./components/admin/ImpersonationBanner";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ImpersonationBanner />
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/jobseeker-auth" element={<JobSeekerAuth />} />
          <Route path="/jobseeker-login" element={<JobSeekerAuth />} />
          <Route path="/jobseeker-signup" element={<JobSeekerSignup />} />
          <Route path="/employer-auth" element={<EmployerAuth />} />
          <Route path="/employer-login" element={<EmployerAuth />} />
          <Route path="/employer-signup" element={<EmployerSignup />} />
          <Route path="/community-org-auth" element={<CommunityOrgAuth />} />
          <Route path="/community-org-signup" element={<CommunityOrgSignup />} />
          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          <Route path="/edit-profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
          <Route path="/connect" element={<ProtectedRoute><Connect /></ProtectedRoute>} />
          <Route path="/profile/:id" element={<ProtectedRoute><ProfileView /></ProtectedRoute>} />
          <Route path="/connections" element={<ProtectedRoute><Connections /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/messages/:id" element={<ProtectedRoute><ConversationView /></ProtectedRoute>} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/legal-guidance" element={<LegalGuidance />} />
          <Route path="/subject-rights" element={<SubjectRights />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/feedback" element={<Feedback />} />
          <Route path="/job-search" element={<JobSearch />} />
          <Route path="/job-application" element={<JobApplication />} />
          <Route path="/ai-resume-builder" element={<AIResumeBuilder />} />
          <Route path="/interview-prep" element={<InterviewPrep />} />
          <Route path="/progressive-disclosure" element={<ProgressiveDisclosure />} />
          <Route path="/progressive-disclosure-info" element={<ProgressiveDisclosureInfo />} />
          
          {/* Protected role-based dashboards - RBAC enforcement */}
          <Route 
            path="/candidate/dashboard" 
            element={
              <ProtectedRoute allowedRole="candidate">
                <CandidateDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/employer/dashboard" 
            element={
              <ProtectedRoute allowedRole="employer_service_provider">
                <EmployerDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/employer/post-job" 
            element={
              <ProtectedRoute allowedRole="employer_service_provider">
                <PostJob />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/employer/find-talent" 
            element={
              <ProtectedRoute allowedRole="employer_service_provider">
                <FindTalent />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/employer/find-talent/:id" 
            element={
              <ProtectedRoute allowedRole="employer_service_provider">
                <FindTalent />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/approvals" 
            element={
              <ProtectedRoute allowedRole="admin">
                <AdminApprovals />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/impersonation" 
            element={
              <ProtectedRoute allowedRole="admin">
                <AdminImpersonation />
              </ProtectedRoute>
            } 
          />
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
